<!--
InputSlider Component — wraps PrimeVue Slider for numeric range selection.

Use for bounded numeric input where a draggable handle is more ergonomic
than typing (volume, percentage, rating thresholds). Supports v-model
with single number or [min, max] tuple in range mode.
-->

<template>
  <PrimeSlider
    :id="elemId"
    v-model="internalValue"
    :min="min"
    :max="max"
    :step="step"
    :range="range"
    :disabled="isDisabled"
    :orientation="orientation"
    @update:modelValue="handleUpdate"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import PrimeSlider from 'primevue/slider';

type SliderValue = number | number[];

interface Props {
  elemId?: string;
  modelValue?: SliderValue;
  min?: number;
  max?: number;
  step?: number;
  range?: boolean;
  disabled?: boolean | string;
  orientation?: 'horizontal' | 'vertical';
}

const props = withDefaults(defineProps<Props>(), {
  elemId: undefined,
  modelValue: 0,
  min: 0,
  max: 100,
  step: 1,
  range: false,
  disabled: false,
  orientation: 'horizontal'
});

const emit = defineEmits<{
  'update:modelValue': [value: SliderValue];
}>();

const internalValue = ref<SliderValue>(props.modelValue ?? (props.range ? [props.min, props.max] : props.min));

const isDisabled = computed<boolean>(
  () => props.disabled === true || props.disabled === 'true'
);

const handleUpdate = (value: SliderValue): void => {
  emit('update:modelValue', value);
};

watch(() => props.modelValue, (next) => {
  if (next !== undefined) internalValue.value = next;
});
</script>
